import { useQuery } from 'react-query';
import { EisRuleListItem } from '../_types';
import { performanceReportingApiUrl, fetcher, handleError, ServerErrorResponse } from 'data_sources/constants';

// Fetch EIS rule by ID from API
export const getEisRuleById = async (
  ruleId: number,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<EisRuleListItem> => {
  try {
    const url = `${performanceReportingApiUrl()}/performance/eis/v1/rules/${ruleId}`;
    
    const response = await fetcher({
      method: 'GET',
      url,
      options: { signal },
    });

    if (response.errorDetails) {
      if (onError) onError(response);
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    } else if (onError && error instanceof Error) {
      handleError(error, { onError });
    }
    throw error;
  }
};

export const useEisRule = (
  ruleId: number,
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) => {
  return useQuery({
    queryKey: ['eisRule', ruleId],
    queryFn: ({ signal }) => getEisRuleById(ruleId, signal, onError),
    enabled: !!ruleId,
    retry: (failureCount, error) => {
      // Don't retry on server errors (5xx) or client errors (4xx)
      const statusCode = (error as any)?.status || (error as any)?.response?.status;
      if (statusCode >= 400) {
        return false;
      }
      return failureCount < 3;
    },
    ...options,
  });
};
